import { _ as _defineProperty } from '../../defineProperty-DmV7cVMj.js';
import { _ as _slicedToArray } from '../../slicedToArray-DvtHmgP0.js';
import { _ as _objectWithoutProperties } from '../../objectWithoutProperties-DN7d1SzH.js';
import { _ as _asyncToGenerator, a as _regeneratorRuntime } from '../../index-B0Q3DaKR.js';
import { useContext, createContext, useCallback, useState, useMemo, useEffect, useLayoutEffect } from 'react';
import noop from '../../noop/index.js';
import useForceUpdate from '../../useForceUpdate/index.js';
import { getCookie, setCookie, deleteCookie } from '../../cookie/index.js';
import IS_BROWSER from '../../isBrowser/index.js';
import { rtlLanguages, languages } from '../../languages/index.js';
import Logger from '../logger/index.js';
import createFetch from '../../createFetch/index.js';
import useMount from '../../useMount/index.js';
import { j as jsxRuntimeExports } from '../../jsx-runtime-DPHDmO8O.js';

var _excluded = ["children", "projectName", "force", "language", "defaultTranslatedData", "mergeDefaultTranslatedData", "withConsole", "withoutReusableComponents", "requestHeader"],
  _excluded2 = ["browserLanguage", "getLocale", "setLocale", "currentLanguage", "projectName"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var initialized = false;
var fetch = createFetch();
var baseUrl = process.env.REACT_APP_API_URL || process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || '';
var DEFAULT_LOCALE = 'en';
var LANGUAGE_COOKIE_NAME = 'language';
var CURRENT_LANGUAGE_COOKIE_NAME = 'currentLanguage';
var SECONDARY_PATHS_FOR_LANGUAGE_COOKIE_NAME = ['/create', '/create/video', '/create/editor'];
var getDefaultLanguageFromBrowser = function getDefaultLanguageFromBrowser() {
  var browserLanguage = IS_BROWSER && navigator.language;
  if (browserLanguage) {
    var lowerCaseBrowserLanguage = browserLanguage.toLocaleLowerCase();
    if (lowerCaseBrowserLanguage === 'zh-hant' || lowerCaseBrowserLanguage === 'zh_hant') {
      // TODO it's temporary solution, we will remove this after mapping this part in backend
      return 'zh_HANT';
    }
    var languagesData = Object.keys(languages);
    var lang = (browserLanguage.includes('-') ? browserLanguage.slice(0, browserLanguage.indexOf('-')) : browserLanguage).toLowerCase();
    if (languagesData.includes(lang)) {
      return lang;
    }
  }
  return DEFAULT_LOCALE;
};
var init = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var _ref$force, force, _ref$multipleRequests, multipleRequests, localesUrl, withConsole, requestHeader, messages;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _ref$force = _ref.force, force = _ref$force === undefined ? false : _ref$force, _ref$multipleRequests = _ref.multipleRequests, multipleRequests = _ref$multipleRequests === undefined ? false : _ref$multipleRequests, localesUrl = _ref.localesUrl, withConsole = _ref.withConsole, requestHeader = _ref.requestHeader;
          if (!(!force && initialized)) {
            _context.next = 3;
            break;
          }
          return _context.abrupt("return");
        case 3:
          _context.prev = 3;
          _context.next = 6;
          return fetch(localesUrl, {
            withoutLanguageHeader: false,
            headers: requestHeader
          });
        case 6:
          messages = _context.sent;
          if (!multipleRequests) {
            initialized = true;
          }
          return _context.abrupt("return", messages);
        case 11:
          _context.prev = 11;
          _context.t0 = _context["catch"](3);
          Logger.error(_context.t0, withConsole);
        case 14:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[3, 11]]);
  }));
  return function init(_x) {
    return _ref2.apply(this, arguments);
  };
}();
var ReactINTLContext = /*#__PURE__*/createContext({});
var translations = new Map();
function ReactINTLProvider(_ref3) {
  var _ref3$children = _ref3.children,
    children = _ref3$children === undefined ? null : _ref3$children,
    _ref3$projectName = _ref3.projectName,
    projectName = _ref3$projectName === undefined ? '' : _ref3$projectName,
    _ref3$force = _ref3.force,
    force = _ref3$force === undefined ? false : _ref3$force,
    language = _ref3.language,
    defaultTranslatedData = _ref3.defaultTranslatedData,
    mergeDefaultTranslatedData = _ref3.mergeDefaultTranslatedData,
    withConsole = _ref3.withConsole,
    _ref3$withoutReusable = _ref3.withoutReusableComponents,
    withoutReusableComponents = _ref3$withoutReusable === undefined ? false : _ref3$withoutReusable,
    requestHeader = _ref3.requestHeader,
    props = _objectWithoutProperties(_ref3, _excluded);
  var forceUpdate = useForceUpdate();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isTranslationsFetched = _useState2[0],
    setIsTranslationsFetched = _useState2[1];
  var getLocale = useCallback(function (key) {
    return getCookie(key);
  }, []);
  var setLocale = useCallback(function (key, value) {
    setIsTranslationsFetched(false);
    setCookie(key, value);
    forceUpdate();
  }, [forceUpdate]);
  useMount(function () {
    deleteCookie(LANGUAGE_COOKIE_NAME, SECONDARY_PATHS_FOR_LANGUAGE_COOKIE_NAME);
    deleteCookie(CURRENT_LANGUAGE_COOKIE_NAME, SECONDARY_PATHS_FOR_LANGUAGE_COOKIE_NAME);
  });
  var languageFromStorage = getLocale(LANGUAGE_COOKIE_NAME);
  var browserLanguage = useMemo(getDefaultLanguageFromBrowser, []);
  var currentLanguage = language || languageFromStorage || browserLanguage;
  var loadTranslations = useCallback(function (withConsole) {
    return init({
      force: force,
      multipleRequests: true,
      localesUrl: "".concat(baseUrl, "/localizations/").concat(currentLanguage, "/messages?project=").concat(withoutReusableComponents ? '' : 'reusable_components').concat(projectName ? ",".concat(projectName) : ''),
      withConsole: withConsole,
      requestHeader: requestHeader
    });
  }, [force, currentLanguage, projectName, requestHeader, withoutReusableComponents]);
  if (defaultTranslatedData && !mergeDefaultTranslatedData) {
    translations.set(currentLanguage, defaultTranslatedData);
  }
  useEffect(function () {
    _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var messages, translationsData;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            setLocale(CURRENT_LANGUAGE_COOKIE_NAME, currentLanguage);
            _context2.t0 = translations.get(currentLanguage);
            if (_context2.t0) {
              _context2.next = 6;
              break;
            }
            _context2.next = 5;
            return loadTranslations(withConsole);
          case 5:
            _context2.t0 = _context2.sent;
          case 6:
            messages = _context2.t0;
            translationsData = defaultTranslatedData && mergeDefaultTranslatedData ? _objectSpread(_objectSpread({}, messages), defaultTranslatedData) : messages;
            translations.set(currentLanguage, translationsData);
            setLocale(CURRENT_LANGUAGE_COOKIE_NAME, currentLanguage);
            setIsTranslationsFetched(true);
            forceUpdate();
          case 12:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }))();
  }, [forceUpdate, currentLanguage, loadTranslations, setLocale, defaultTranslatedData, mergeDefaultTranslatedData, withConsole]);
  useLayoutEffect(function () {
    if (rtlLanguages.includes(currentLanguage)) {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  }, [currentLanguage]);
  var translate = useCallback(function () {
    return translations.get(currentLanguage);
  }, [currentLanguage]);
  var reactINTLContextProviderValue = useMemo(function () {
    return _objectSpread({
      translate: translate,
      browserLanguage: browserLanguage,
      getLocale: getLocale,
      setLocale: setLocale,
      currentLanguage: currentLanguage,
      projectName: projectName,
      isTranslationsFetched: isTranslationsFetched
    }, props);
  }, [browserLanguage, currentLanguage, getLocale, isTranslationsFetched, projectName, props, setLocale, translate]);
  return /*#__PURE__*/jsxRuntimeExports.jsx(ReactINTLContext.Provider, {
    value: reactINTLContextProviderValue,
    children: children
  });
}
var useTranslator = function useTranslator() {
  var _useContext = useContext(ReactINTLContext),
    _useContext$translate = _useContext.translate,
    tr = _useContext$translate === undefined ? noop : _useContext$translate;
  var translatedObj = tr();
  var translate = useCallback(function (str, values) {
    var translatedLocale = translatedObj && translatedObj[str] ? translatedObj[str] : str;
    var dynamics = Object.entries(values || {});
    if (!dynamics.length) return translatedLocale;
    return dynamics.reduce(function (result, _ref5) {
      var _ref6 = _slicedToArray(_ref5, 2),
        key = _ref6[0],
        value = _ref6[1];
      var re = new RegExp("{{".concat(key, "}}"), 'g');
      // eslint-disable-next-line
      var finalResult = result.replace('href={`${basePath}terms-of-use`}', 'href="{{basePath}}privacy-policy\"').replace(re, value);
      return finalResult;
    }, translatedLocale);
  }, [translatedObj]);
  return translate;
};
var useIsTranslationsFetched = function useIsTranslationsFetched() {
  var _useContext2 = useContext(ReactINTLContext),
    isTranslationsFetched = _useContext2.isTranslationsFetched;
  return isTranslationsFetched;
};
var useLanguageByUser = function useLanguageByUser() {
  var _useContext3 = useContext(ReactINTLContext),
    browserLanguage = _useContext3.browserLanguage,
    getLocale = _useContext3.getLocale,
    setLocale = _useContext3.setLocale,
    currentLanguage = _useContext3.currentLanguage,
    projectName = _useContext3.projectName,
    props = _objectWithoutProperties(_useContext3, _excluded2);
  return _objectSpread({
    browserLanguage: browserLanguage,
    getLocale: getLocale,
    setLocale: setLocale,
    currentLanguage: currentLanguage,
    projectName: projectName
  }, props);
};
var getTranslationsJSON = function getTranslationsJSON(_ref7) {
  var language = _ref7.language,
    projectName = _ref7.projectName,
    withConsole = _ref7.withConsole,
    requestHeader = _ref7.requestHeader;
  return init({
    force: false,
    multipleRequests: true,
    localesUrl: "".concat(baseUrl, "/localizations/").concat(language, "/messages?project=reusable_components").concat(projectName ? ",".concat(projectName) : ''),
    withConsole: withConsole,
    requestHeader: requestHeader
  });
};

export { CURRENT_LANGUAGE_COOKIE_NAME, DEFAULT_LOCALE, LANGUAGE_COOKIE_NAME, ReactINTLProvider, SECONDARY_PATHS_FOR_LANGUAGE_COOKIE_NAME, getDefaultLanguageFromBrowser, getTranslationsJSON, init, useIsTranslationsFetched, useLanguageByUser, useTranslator };
//# sourceMappingURL=index.js.map
